import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type { ClaireLiveQualityReport } from '@borradh-workspace/api-client/types';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

export const fetchClaireLiveQuality = (
  options: { refresh?: boolean } = {},
  signal?: AbortSignal
) =>
  apiClient.get<ClaireLiveQualityReport>(
    `admin-terminal/claire-live-quality${options.refresh ? '?refresh=true' : ''}`,
    { signal }
  );

export const claireLiveQualityQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.claireLiveQuality(),
    queryFn: ({ signal }) => fetchClaireLiveQuality({}, signal),
    // Whole days only: nothing on the page moves within the hour.
    staleTime: 10 * 60_000,
  });

export const useClaireLiveQuality = () =>
  useQuery(claireLiveQualityQueryOptions());

/** Re-read the two newest days from Langfuse, past the API's cache. */
export const useRefreshClaireLiveQuality = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => fetchClaireLiveQuality({ refresh: true }),
    onSuccess: (report) =>
      queryClient.setQueryData(
        queryKeys.adminTerminal.claireLiveQuality(),
        report
      ),
    onError: (error: Error) =>
      toast.error(error.message || "Could not refresh Claire's live numbers"),
  });
  return { refresh: mutation.mutate, isRefreshing: mutation.isPending };
};
